import { normalizar } from '../models/deterministic.model';
import { estadoDe, regionDe, zonaDe } from './jerarquia.data';
import { NPS_SUCURSALES } from './home.data';
import { RANKING_SUCURSAL } from './results.data';
import { STORE_RESULTS_ROWS } from './store-results.data';

/**
 * Catálogo de sucursales: a qué región, zona y estado pertenece cada una.
 *
 * Las tablas del tablero están partidas por pantalla y sólo algunas traen la
 * jerarquía: las alertas, los planes y los comentarios sí (`region`, `zona`,
 * `sucursal`, `estado`), pero el NPS por sucursal, el ranking de nueve columnas
 * y el CSAT por tienda traen únicamente el nombre. Sin este catálogo, filtrar
 * por «Región» sólo podría afectar a tres de las quince tablas.
 *
 * Ya no hay que cosechar nada de las tablas: **todas** —las que traen jerarquía
 * y las que no— se colocan con las mismas funciones de `jerarquia.data.ts`, así
 * que basta recorrer el universo de nombres. Antes se cosechaba primero y se
 * rellenaba después, y las dos vías no coincidían: la misma tienda salía en una
 * zona en la tabla de alertas y en otra al filtrar.
 */
export interface ISucursalDim {
  sucursal: string;
  region: string;
  zona: string;
  estado: string;
}

/* -------------------------------------------------------------------------- */
/*                              ANCHOR catalogo                               */
/* -------------------------------------------------------------------------- */

/** Todas las sucursales que el tablero llega a pintar en alguna tabla. */
const UNIVERSO: Array<string> = [
  ...NPS_SUCURSALES.map((fila) => fila.sucursal),
  ...RANKING_SUCURSAL.map((fila) => fila.sucursal),
  ...STORE_RESULTS_ROWS.map((fila) => fila.sucursal),
];

const catalogo = new Map<string, ISucursalDim>();

for (const sucursal of UNIVERSO) {
  const clave = normalizar(sucursal);

  if (!catalogo.has(clave)) {
    catalogo.set(clave, {
      sucursal,
      region: regionDe(sucursal),
      zona: zonaDe(sucursal),
      estado: estadoDe(sucursal),
    });
  }
}

/* -------------------------------------------------------------------------- */
/*                                 ANCHOR API                                 */
/* -------------------------------------------------------------------------- */

/** Catálogo indexado por nombre normalizado de sucursal. */
export const DIMENSION_SUCURSAL: ReadonlyMap<string, ISucursalDim> = catalogo;

export const dimensionDe = (sucursal: string): ISucursalDim | undefined =>
  catalogo.get(normalizar(sucursal));

/**
 * Nombre del estado en el GeoJSON del mapa, por estado de la tabla.
 *
 * La coropleta usa los nombres oficiales del archivo de geometrías («Ciudad de
 * México», «México») y las tablas y el filtro usan los del programa («CDMX»,
 * «ESTADO DE MÉXICO»). Sin esta tabla, filtrar por estado apagaría el mapa
 * entero: ninguna de las dos formas encuentra a la otra.
 */
const MAPA_POR_ESTADO: Record<string, string> = {
  'CDMX': 'Ciudad de México',
  'ESTADO DE MEXICO': 'México',
  'GUANAJUATO': 'Guanajuato',
  'GUERRERO': 'Guerrero',
  'HIDALGO': 'Hidalgo',
  'JALISCO': 'Jalisco',
  'MICHOACAN': 'Michoacán',
  'MORELOS': 'Morelos',
  'PUEBLA': 'Puebla',
  'QUERETARO': 'Querétaro',
};

export const nombreEnMapa = (estado: string): string =>
  MAPA_POR_ESTADO[normalizar(estado)] ?? estado;

/**
 * Sucursales que caen dentro de una selección de región, zona y/o estado.
 * Devuelve nombres **normalizados**, que es como se cruzan las tablas.
 */
export const sucursalesDe = (criterios: {
  regiones?: Array<string>;
  zonas?: Array<string>;
  estados?: Array<string>;
}): Set<string> => {
  const regiones = new Set((criterios.regiones ?? []).map(normalizar));
  const zonas = new Set((criterios.zonas ?? []).map(normalizar));
  const estados = new Set((criterios.estados ?? []).map(normalizar));
  const resultado = new Set<string>();

  for (const [clave, dim] of catalogo) {
    if (regiones.size && !regiones.has(normalizar(dim.region))) {
      continue;
    }

    if (zonas.size && !zonas.has(normalizar(dim.zona))) {
      continue;
    }

    if (estados.size && !estados.has(normalizar(dim.estado))) {
      continue;
    }

    resultado.add(clave);
  }

  return resultado;
};
